import React, { useState } from 'react';
import { Mail, Phone, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { newsletterAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const NewsletterSignup = ({ variant = 'default' }) => {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    preferredContact: 'email'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await newsletterAPI.subscribe(formData);
      toast({
        title: t('newsletter.success'),
        description: t('newsletter.successDesc'),
      });
      // Reset form
      setFormData({
        email: '',
        phone: '',
        preferredContact: 'email'
      });
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      toast({
        title: language === 'es' ? "Error de suscripción" : "Subscription Failed",
        description: language === 'es' ? "Hubo un error. Por favor intente de nuevo." : "There was an error. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Inline variant (for footer or CTA sections)
  if (variant === 'inline') {
    return (
      <form onSubmit={handleSubmit} className="space-y-5">
        <h3 className="text-xl font-black text-white mb-2 uppercase tracking-wide">{t('newsletter.title')}</h3>
        <p className="text-base text-gray-300 mb-5 leading-relaxed font-medium">
          {t('newsletter.subtitle')}
        </p>
        
        <div className="space-y-4">
          <Input
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-12 text-base font-medium"
            placeholder={t('newsletter.emailPlaceholder')}
          />
          
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-12 text-base font-medium"
            placeholder={t('newsletter.phonePlaceholder')}
          />

          <Select value={formData.preferredContact} onValueChange={(value) => handleChange('preferredContact', value)}>
            <SelectTrigger className="bg-white/10 border-white/30 text-white h-12 text-base font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email" className="text-base">{t('newsletter.emailOption')}</SelectItem>
              <SelectItem value="text" className="text-base">{t('newsletter.textOption')}</SelectItem>
              <SelectItem value="both" className="text-base">{t('newsletter.bothOption')}</SelectItem>
            </SelectContent>
          </Select>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold transition-all duration-300 hover:scale-105 h-12 text-base"
          >
            <span>{submitting ? t('newsletter.subscribing') : t('newsletter.subscribe')}</span>
            <Send size={18} className="ml-2" />
          </Button>
        </div>
      </form>
    );
  }

  // Full variant (for dedicated section)
  return (
    <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-[#84bc00] to-[#6a9600] rounded-3xl p-10 md:p-16 shadow-2xl">
          <div className="text-center mb-10">
            <h2 className="text-4xl sm:text-5xl font-black text-black mb-4">
              {t('newsletter.title')}
            </h2>
            <p className="text-xl text-black/80">
              {t('newsletter.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="email" className="text-black font-bold mb-2 flex items-center space-x-2">
                  <Mail size={18} />
                  <span>{t('newsletter.emailOption').toUpperCase()} *</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="border-2 border-black/20 focus:border-black transition-colors"
                  placeholder={t('newsletter.emailPlaceholder')}
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-black font-bold mb-2 flex items-center space-x-2">
                  <Phone size={18} />
                  <span>{t('newsletter.phonePlaceholder').toUpperCase()}</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="border-2 border-black/20 focus:border-black transition-colors"
                  placeholder={language === 'es' ? 'Para mensajes de texto' : 'For text messages'}
                />
              </div>
            </div>

            <div>
              <Label className="text-black font-bold mb-2">{t('newsletter.preferredContact').toUpperCase()}</Label>
              <Select value={formData.preferredContact} onValueChange={(value) => handleChange('preferredContact', value)}>
                <SelectTrigger className="border-2 border-black/20 focus:border-black">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">📧 {t('newsletter.emailOption')}</SelectItem>
                  <SelectItem value="text">📱 {t('newsletter.textOption')}</SelectItem>
                  <SelectItem value="both">📧📱 {t('newsletter.bothOption')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-black hover:bg-white text-[#84bc00] hover:text-black text-lg font-bold py-7 transition-all duration-300 hover:scale-105 group"
            >
              <span>{submitting ? t('newsletter.subscribing') : t('newsletter.subscribe')}</span>
              <Send size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>

            <p className="text-center text-sm text-black/70">
              {language === 'es' 
                ? '✅ Solo ofertas mensuales • ❌ Sin llamadas de marketing • 🔒 Cancela cuando quieras'
                : '✅ Monthly specials only • ❌ No marketing calls • 🔒 Unsubscribe anytime'}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
